/** 내기 결과 공개 연출의 타이밍.
 *
 * **결과는 뽑는 순간 이미 확정된다.** 여기 있는 건 그 확정된 값을 언제 화면에
 * 내보낼지 뿐이다 — 연출 중에 다시 뽑거나 값을 바꾸면 그건 조작이다.
 *
 * 이 파일이 **화면 움직임과 공개 시각의 유일한 출처**다. CSS 지연값도 여기서 나온
 * 식을 쓴다. 두 벌로 계산하면 선이 바닥에 닿기 전에 칸이 열리거나, 슬롯이 멈추기 전에
 * 이름이 확정돼서 연출이 거짓말을 한다.
 */

/** 도입 구간(leadMs)이 지나면 `stepMs` 간격으로 하나씩 확정된다.
 *
 * 두 모드가 같은 모양이다 — 제비뽑기는 슬롯이 돌다 착지하고, 사다리는 선이 내려가
 * 도착한다. 다른 건 상수뿐이라 계산도 테스트도 한 벌로 끝난다.
 */
export type RevealTiming = { leadMs: number; stepMs: number };

/** 슬롯이 도는 시간과 다음 슬롯이 멈추는 간격.
 *  1.6초는 슬롯이 감속하는 게 눈에 보이는 하한쯤이다. 더 늘리면 두 번째 뽑기부터
 *  지루해진다 — 이 화면은 한 자리에서 여러 번 눌린다. */
export const LOTS_TIMING: RevealTiming = { leadMs: 1600, stepMs: 260 };

/** 사다리는 도입을 짧게 두고 경로가 드라마를 맡는다. 경로 하나가 0.45초라
 *  4명이면 약 2.4초 — 사람이 늘어도 `바로 보기` 로 건너뛸 수 있다. */
export const LADDER_TIMING: RevealTiming = { leadMs: 600, stepMs: 450 };

export type RevealPhase = "rolling" | "revealing" | "done";
export type RevealState = { phase: RevealPhase; revealed: number };

/** `index` 번째가 확정되는 시각. 슬롯이 멈추고, 선이 바닥에 닿는 그 순간이다.
 *  CSS 지연값도 이 식에서 나온다. */
export function landingMs(index: number, timing: RevealTiming): number {
  return timing.leadMs + index * timing.stepMs;
}

/** 연출이 다 끝나는 시각 = 마지막 착지 시각. */
export function revealDurationMs(count: number, timing: RevealTiming): number {
  return count <= 0 ? 0 : landingMs(count - 1, timing);
}

/** 시작 후 `elapsedMs` 시점에 몇 개가 공개됐는지. */
export function revealAt(
  elapsedMs: number,
  count: number,
  timing: RevealTiming
): RevealState {
  if (count <= 0) return { phase: "done", revealed: 0 };
  if (elapsedMs < timing.leadMs) return { phase: "rolling", revealed: 0 };
  const revealed = Math.min(
    count,
    Math.floor((elapsedMs - timing.leadMs) / timing.stepMs) + 1
  );
  return { phase: revealed >= count ? "done" : "revealing", revealed };
}

/** 다음으로 화면이 바뀌어야 하는 시각. 남은 게 없으면 `null`.
 *
 * 고정 주기로 틱을 돌리지 않는 이유: 움직임은 CSS 가 그리므로 JS 는 착지 순간에만
 * 깨어나면 된다. 80ms 틱이면 렌더가 수십 번 헛돌고, 틱 경계가 CSS 착지 시각과
 * 어긋나서 칸이 한 프레임 먼저/늦게 열린다.
 */
export function nextBoundaryMs(
  elapsedMs: number,
  count: number,
  timing: RevealTiming
): number | null {
  for (let index = 0; index < count; index++) {
    const at = landingMs(index, timing);
    if (at > elapsedMs) return at;
  }
  return null;
}

/** CSS 애니메이션에 그대로 넣는 값. `delayMs + durationMs` 가 곧 착지 시각이라
 *  화면의 움직임이 끝나는 순간과 `revealAt` 이 확정하는 순간이 같아진다. */
export type CssTiming = { delayMs: number; durationMs: number };

/** 슬롯 하나. 전부 t=0 에 같이 돌기 시작하고 순서대로 멈춘다 —
 *  그래서 지연이 아니라 **길이**가 슬롯마다 다르다. */
export function reelTiming(index: number, timing: RevealTiming): CssTiming {
  return { delayMs: 0, durationMs: landingMs(index, timing) };
}

/** 경로 하나. 앞 사람이 도착한 뒤에 출발해서 `stepMs` 동안 내려간다. */
export function pathTiming(index: number, timing: RevealTiming): CssTiming {
  const finish = landingMs(index, timing);
  // 길이를 먼저 정하고 지연을 역산한다. leadMs < stepMs 로 상수를 바꾸면 첫 경로가
  // 음수 지연이 되어 착지가 어긋나므로, 그때는 테스트가 먼저 깨진다.
  return { delayMs: finish - timing.stepMs, durationMs: timing.stepMs };
}

/** 움직임을 줄여 달라고 설정한 사용자에게는 연출을 걸지 않는다. 결과는 그대로 보인다 —
 *  기다리는 시간이 사라질 뿐이다. */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
